import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useCurrency } from '../../../../context/CurrencyContext';
import { getServicesFromDB } from '../../../../services/pricingService';
import './OurServices.css';

const OurServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedCurrency, formatPrice, convertPrice, isLoading: currencyLoading } = useCurrency();

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const servicesData = await getServicesFromDB();
      
      console.log('Raw services data from DB:', servicesData);
      
      if (servicesData && servicesData.length > 0) {
        // Validate the data structure
        const validServices = servicesData.filter(service => {
          const isValid = service && service.slug && service.title;
          if (!isValid) {
            console.warn('Invalid service data:', service);
          }
          return isValid;
        });
        
        if (validServices.length > 0) {
          console.log('Valid services found:', validServices);
          setServices(validServices);
        } else {
          console.log('No valid services found in DB, using default services');
          setServices(getDefaultServices());
        }
      } else {
        console.log('No services found in DB, using default services');
        setServices(getDefaultServices());
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      // Fallback to default services if DB fails
      setServices(getDefaultServices());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultServices = () => [
    {
      id: 1,
      slug: 'consultation',
      title: 'Doctor Consultation',
      description: 'Book appointments with top-rated specialists across all medical fields',
      basePrice: 500,
      baseCurrency: 'INR',
      features: [
        '24/7 appointment booking',
        'Verified doctor profiles',
        'Instant confirmation',
        'Reschedule flexibility'
      ],
      icon: '👨‍⚕️',
      category: 'consultation'
    },
    {
      id: 2,
      slug: 'management',
      title: 'Hospital Management',
      description: 'Comprehensive hospital management solutions for healthcare providers',
      basePrice: 2500,
      baseCurrency: 'INR',
      features: [
        'Patient management system',
        'Appointment scheduling',
        'Billing & invoicing',
        'Staff management'
      ],
      icon: '🏥',
      category: 'management'
    },
    {
      id: 3,
      slug: 'telemedicine',
      title: 'Telemedicine Services',
      description: 'Virtual consultations with healthcare professionals from anywhere',
      basePrice: 800,
      baseCurrency: 'INR',
      features: [
        'HD video calls',
        'Secure messaging',
        'Prescription delivery',
        'Medical records access'
      ],
      icon: '📱',
      category: 'telemedicine'
    },
    {
      id: 4,
      slug: 'insurance',
      title: 'Health Insurance',
      description: 'Comprehensive health coverage plans for individuals and families',
      basePrice: 1500,
      baseCurrency: 'INR',
      features: [
        'Multiple plan options',
        'Cashless hospitalization',
        'Pre & post hospitalization',
        '24/7 customer support'
      ],
      icon: '🛡️',
      category: 'insurance'
    },
    {
      id: 5,
      slug: 'lab',
      title: 'Laboratory Services',
      description: 'Home sample collection and lab testing services',
      basePrice: 300,
      baseCurrency: 'INR',
      features: [
        'Home sample collection',
        'Quick results delivery',
        'Certified laboratories',
        'Affordable pricing'
      ],
      icon: '🧪',
      category: 'lab'
    },
    {
      id: 6,
      slug: 'pharmacy',
      title: 'Pharmacy Delivery',
      description: 'Fast delivery of medicines and healthcare products',
      basePrice: 200,
      baseCurrency: 'INR',
      features: [
        'Same-day delivery',
        'Genuine medicines',
        'Prescription verification',
        'Discount offers'
      ],
      icon: '💊',
      category: 'pharmacy'
    }
  ];

  const getDisplayPrice = async (service) => {
    if (service.baseCurrency === selectedCurrency) {
      return { amount: service.basePrice, symbol: getCurrencySymbol(selectedCurrency) };
    }

    try {
      const convertedAmount = await convertPrice(service.basePrice, service.baseCurrency, selectedCurrency);
      return { amount: convertedAmount, symbol: getCurrencySymbol(selectedCurrency) };
    } catch (error) {
      console.error('Price conversion failed:', error);
      return { amount: service.basePrice, symbol: getCurrencySymbol(service.baseCurrency) };
    }
  };

  const getCurrencySymbol = (currency) => {
    const currencyMap = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'INR': '₹', 
      'JPY': '¥', 'AUD': 'A$', 'CAD': 'C$'
    };
    return currencyMap[currency] || '₹';
  };

  if (loading) {
    return (
      <section className="our-services">
        <div className="container">
          <div className="services-header" style={{ marginBottom: "2rem", textAlign: "center" }}>
            <h2 className="section-title">Loading Services...</h2>
            <div className="loading-spinner">⏳</div>
          </div>
        </div>
      </section>
    );
  }


  
  return (
    <section className="our-services">
      <div className="container">
        <div className="services-header" style={{ marginBottom: "2rem", textAlign: "center" }}>
          <h2 className="section-title">
            <span className="healthcare-text">Our Healthcare</span>{' '}
            <span className="services-text">Services</span>
          </h2>
          <p className="section-description">
            Comprehensive healthcare solutions tailored to your needs
            <span className="currency-note" style={{ display: 'block', fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
              Prices shown in {selectedCurrency}
              {currencyLoading && <span style={{ marginLeft: '0.5rem' }}>🔄</span>}
            </span>
          </p>
        </div>

        <div className="services-grid">
          {services.map((service) => (
            <ServiceCard 
              key={service.id} 
              service={service} 
              selectedCurrency={selectedCurrency}
              getDisplayPrice={getDisplayPrice}
              getCurrencySymbol={getCurrencySymbol}
            />
          ))}
        </div>

        <div className="services-cta">
          <div className="cta-content">
            <h3>Need a Custom Solution?</h3>
            <p>Contact our team to discuss your specific healthcare requirements and get a personalized quote.</p>
            <div className="cta-buttons">
              <button className="cta-button primary">
                <span>Contact Sales</span>
                <i className="fas fa-phone"></i>
              </button>
              <button className="cta-button secondary">
                <span>Schedule Demo</span>
                <i className="fas fa-calendar"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Separate component for service card with dynamic pricing
const ServiceCard = ({ service, selectedCurrency, getDisplayPrice, getCurrencySymbol }) => {
  const [displayPrice, setDisplayPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);

  useEffect(() => {
    loadPrice();
  }, [service, selectedCurrency]);

  const loadPrice = async () => {
    setPriceLoading(true);
    try {
      const price = await getDisplayPrice(service);
      setDisplayPrice(price);
    } catch (error) {
      console.error('Error loading price:', error);
      setDisplayPrice({ amount: service.basePrice, symbol: getCurrencySymbol(service.baseCurrency) });
    } finally {
      setPriceLoading(false);
    }
  };

  if (!displayPrice) {
    return (
      <div className="service-card">
        <div className="service-content">
          <h3 className="service-title">{service.title}</h3>
          <div className="service-pricing">
            <div className="price">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="service-card">
      <div className="service-icon">
        <span className="icon-emoji">{service.icon}</span>
      </div>
      
      <div className="service-content">
        <h3 className="service-title">{service.title}</h3>
        <p className="service-description">{service.description}</p>
        
        <div className="service-features">
          {service.features.map((feature, index) => (
            <div key={index} className="feature-item">
              <i className="fas fa-check"></i>
              <span>{feature}</span>
            </div>
          ))}
        </div>
        
        <div className="service-pricing">
          <div className="price">
            {displayPrice.symbol}{priceLoading ? '...' : displayPrice.amount.toFixed(2)}
          </div>
          <div className="price-period">per service</div>
          {selectedCurrency !== service.baseCurrency && (
            <div className="original-price" style={{ fontSize: '0.7rem', color: '#666', marginTop: '0.25rem' }}>
              Original: {getCurrencySymbol(service.baseCurrency)}{service.basePrice}
            </div>
          )}
        </div>
        
        <div className="service-actions">
          <button className="service-cta">
            <span>Get Started</span>
            <i className="fas fa-arrow-right"></i>
          </button>
          <Link 
            className="service-detail-link" 
            to={`/services/${service.slug}`}
          >
            View details
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OurServices;
